import { create } from "zustand";
import * as THREE from "three";
import { useGame } from "../../src/stores/useGame";
import { useLevelProgress } from "./useLevelProgress";
import { useUI } from "./useUI";

export enum DialogStep {
  NONE = "none",
  FACT = "fact",
  QUEST = "quest",
  MAP_UNLOCK_NOTIFICATION = "map_unlock_notification"
}

export type DialogSection = {
  facts: string[];
  question: string;
  answers: string[];
  correctAnswerIndex: number;
  answerForIncorrect: string;
};

export type DialogState = {
  isOpen: boolean;
  npcId: string | null;
  npcName: string | null;
  step: DialogStep;
  factIndex: number;
  sectionIndex: number;
  dialogSections: DialogSection[];
  selectedAnswerIndex: number | null;
  cameraTarget?: THREE.Vector3 | null;
  unlockedMapId?: string | null;
  unlockedMapName?: string | null;
};

export type DialogProgress = {
  step: DialogStep;
  factIndex: number;
  sectionIndex: number;
  isCompleted: boolean;
};

export type OpenDialogParams = {
  npcId: string;
  mapId: string;
  npcName?: string | null;
  dialogSections: DialogSection[];
  cameraTarget?: THREE.Vector3 | null;
  onComplete?: () => void;
};

// Interface definition moved to the top
interface DialogStore {
  // State
  dialog: DialogState;
  onCompleteCallback: (() => void) | null;
  currentMapId: string | null;

  // Actions
  openDialog: (params: OpenDialogParams) => void;
  startFacts: () => void;
  nextFact: () => void;
  startQuest: () => void;
  selectAnswer: (answerIndex: number) => void;
  nextQuest: () => void;
  closeDialog: () => void;
  showMapUnlockNotification: (mapId: string, mapName: string) => void;
  resetDialogState: () => void;
  acceptMapTransition: () => void;
  declineMapTransition: () => void;
}

export const useDialogs = create<DialogStore>((set, get) => {
  // Helper function for dialog completion and displaying map unlock notification
  const handleDialogCompletion = (dialog: DialogState, currentMapId: string) => {
    const levelProgress = useLevelProgress.getState();

    // Save the latest progress
    levelProgress.updateNPCDialog(currentMapId, dialog.npcId!, DialogStep.FACT, dialog.factIndex, dialog.sectionIndex);

    // Check if this NPC unlocks a new map BEFORE completing it
    const mapData = levelProgress.getMapData()[currentMapId];
    const npc = mapData?.npcs.find(n => n.id === dialog.npcId);
    const mapToUnlock = npc?.unlocksMapId || null;
    const isMapAlreadyUnlocked = mapToUnlock ? levelProgress.isMapUnlocked(mapToUnlock) : false;
    const shouldShowMapUnlock = Boolean(mapToUnlock && !isMapAlreadyUnlocked);

    // Mark NPC as completed - this will unlock the map if needed
    levelProgress.completeNPC(currentMapId, dialog.npcId!);

    // Show map unlock notification
    if (shouldShowMapUnlock && mapToUnlock) {
      // Get map name - this is a newly unlocked map
      const worldConfig = levelProgress.getWorldConfig();
      const unlockedMap = worldConfig?.maps.find(m => m.id === mapToUnlock);
      const unlockedMapName = unlockedMap?.name || `Map ${mapToUnlock}`;

      // Show map unlock notification
      set({
        dialog: {
          ...dialog,
          step: DialogStep.MAP_UNLOCK_NOTIFICATION,
          unlockedMapId: mapToUnlock,
          unlockedMapName,
        }
      });
    } else {
      // Either NPC doesn't unlock any map or the map is already unlocked
      // Call completion callback if available
      const state = get();
      if (state.onCompleteCallback) {
        state.onCompleteCallback();
      }

      // Show completion notification
      set({
        dialog: {
          ...dialog,
          step: DialogStep.MAP_UNLOCK_NOTIFICATION,
          unlockedMapId: null,
          unlockedMapName: "Task completed!",
        }
      });
    }
  };

  return {
    dialog: {
      isOpen: false,
      npcId: null,
      npcName: null,
      step: DialogStep.NONE,
      factIndex: 0,
      sectionIndex: 0,
      dialogSections: [],
      selectedAnswerIndex: null,
      cameraTarget: null,
      unlockedMapId: null,
      unlockedMapName: null,
    },

    onCompleteCallback: null,
    currentMapId: null,

    openDialog: (params: OpenDialogParams) => {
      // Load progress from centralized store
      const levelProgress = useLevelProgress.getState();
      const npcProgress = levelProgress.getNPCProgress(params.mapId, params.npcId);

      // Start directly with facts
      const step = npcProgress?.isCompleted ? DialogStep.FACT : npcProgress?.dialogStep || DialogStep.FACT;
      const factIndex = npcProgress?.isCompleted ? 0 : npcProgress?.factIndex || 0;
      const sectionIndex = npcProgress?.isCompleted ? 0 : npcProgress?.sectionIndex || 0;

      set({
        dialog: {
          isOpen: true,
          npcId: params.npcId,
          npcName: params.npcName ?? null,
          step: step as DialogStep,
          factIndex: factIndex,
          sectionIndex: sectionIndex,
          dialogSections: params.dialogSections,
          selectedAnswerIndex: null,
          cameraTarget: params.cameraTarget ?? null,
        },
        onCompleteCallback: params.onComplete ?? null,
        currentMapId: params.mapId,
      });
    },

    startFacts: () => {
      const dialog = get().dialog;
      const currentMapId = get().currentMapId;
      if (!dialog.isOpen || !currentMapId) return;

      // Update progress in existing NPC fields
      const levelProgress = useLevelProgress.getState();
      const currentProgress = levelProgress.getNPCProgress(currentMapId, dialog.npcId!);

      if (currentProgress) {
        // Update existing progress, preserve completion status
        levelProgress.updateNPCDialog(currentMapId, dialog.npcId!, DialogStep.FACT, 0);
      }

      set({
        dialog: { ...dialog, step: DialogStep.FACT, factIndex: 0 },
      });
    },

    nextFact: () => {
      const dialog = get().dialog;
      const currentMapId = get().currentMapId;
      if (!dialog.isOpen || !currentMapId) return;

      // Get the current dialog section
      const currentSection = dialog.dialogSections[dialog.sectionIndex];
      const nextFactIndex = dialog.factIndex + 1;

      // Check if this is the last fact in the current section
      if (nextFactIndex >= currentSection.facts.length) {
        // All facts shown, move to the question in the current section
        if (currentSection.question) {
          // If there's a question in the current section, move to it
          const levelProgress = useLevelProgress.getState();
          levelProgress.updateNPCDialog(currentMapId, dialog.npcId!, DialogStep.QUEST, 0);

          set({
            dialog: {
              ...dialog,
              step: DialogStep.QUEST,
              factIndex: 0, // Reset fact index for the next section
              selectedAnswerIndex: null
            },
          });
        } else {
          // If there's no question, move to the next section or complete the dialog
          const nextSectionIndex = dialog.sectionIndex + 1;

          if (nextSectionIndex < dialog.dialogSections.length) {
            // Move to the next section
            const levelProgress = useLevelProgress.getState();
            levelProgress.updateNPCDialog(currentMapId, dialog.npcId!, DialogStep.FACT, 0, nextSectionIndex);

            set({
              dialog: {
                ...dialog,
                sectionIndex: nextSectionIndex,
                factIndex: 0,
                step: DialogStep.FACT,
                selectedAnswerIndex: null
              },
            });
          } else {
            // All sections completed, finish the dialog
            handleDialogCompletion(dialog, currentMapId);
          }
        }
      } else {
        // Show the next fact in the current section
        const levelProgress = useLevelProgress.getState();
        levelProgress.updateNPCDialog(currentMapId, dialog.npcId!, DialogStep.FACT, nextFactIndex);

        set({
          dialog: { ...dialog, factIndex: nextFactIndex },
        });
      }
    },

    // Start quest functionality
    startQuest: () => {
      const dialog = get().dialog;
      const currentMapId = get().currentMapId;
      if (!dialog.isOpen || !currentMapId) return;

      // Update progress in existing NPC fields
      const levelProgress = useLevelProgress.getState();
      levelProgress.updateNPCDialog(currentMapId, dialog.npcId!, DialogStep.QUEST, 0);

      set({
        dialog: {
          ...dialog,
          step: DialogStep.QUEST,
          selectedAnswerIndex: null
        },
      });
    },

    // Select an answer in the current quest
    selectAnswer: (answerIndex: number) => {
      const dialog = get().dialog;
      const currentMapId = get().currentMapId;
      if (!dialog.isOpen || !currentMapId || dialog.step !== DialogStep.QUEST) return;

      // Record the selected answer
      set({
        dialog: { ...dialog, selectedAnswerIndex: answerIndex },
      });
    },

    // Move to the next quest or complete if all quests are done
    nextQuest: () => {
      const dialog = get().dialog;
      const currentMapId = get().currentMapId;
      if (!dialog.isOpen || !currentMapId || dialog.step !== DialogStep.QUEST) return;

      // After answering the question, move to the next section
      const nextSectionIndex = dialog.sectionIndex + 1;

      if (nextSectionIndex < dialog.dialogSections.length) {
        // Move to the next section
        const levelProgress = useLevelProgress.getState();
        levelProgress.updateNPCDialog(currentMapId, dialog.npcId!, DialogStep.FACT, 0, nextSectionIndex);

        set({
          dialog: {
            ...dialog,
            sectionIndex: nextSectionIndex,
            factIndex: 0,
            step: DialogStep.FACT, // Start with facts in the new section
            selectedAnswerIndex: null
          },
        });
      } else {
        // All sections completed, finish the dialog
        handleDialogCompletion(dialog, currentMapId);
      }
    },

    closeDialog: () => {
      const dialog = get().dialog;
      const currentMapId = get().currentMapId;
      if (!dialog.isOpen || !currentMapId) return;

      // Save current progress in existing NPC fields before closing
      if (dialog.step === DialogStep.FACT || dialog.step === DialogStep.QUEST) {
        const levelProgress = useLevelProgress.getState();

        // Save the current fact index
        levelProgress.updateNPCDialog(currentMapId, dialog.npcId!, dialog.step, dialog.factIndex, dialog.sectionIndex);
      }

      // Reset dialog state
      get().resetDialogState();
    },

    showMapUnlockNotification: (mapId: string, mapName: string) => {
      set((state) => ({
        dialog: {
          ...state.dialog,
          step: DialogStep.MAP_UNLOCK_NOTIFICATION,
          unlockedMapId: mapId,
          unlockedMapName: mapName,
        }
      }));
    },

    // Helper function to reset dialog state
    resetDialogState: () => {
      // Clear camera positions
      useGame.getState().setCameraPos(null);
      useGame.getState().setCameraTarget(null);

      // Reset dialog state
      set({
        dialog: {
          isOpen: false,
          npcId: null,
          npcName: null,
          step: DialogStep.NONE,
          factIndex: 0,
          sectionIndex: 0,
          dialogSections: [],
          selectedAnswerIndex: null,
          cameraTarget: null,
          unlockedMapId: null,
          unlockedMapName: null,
        },
        onCompleteCallback: null,
        currentMapId: null,
      });
    },

    acceptMapTransition: () => {
      const dialog = get().dialog;
      const currentMapId = get().currentMapId;
      const unlockedMapId = dialog.unlockedMapId;

      if (!unlockedMapId || !currentMapId) return;

      // Complete the NPC first
      const levelProgress = useLevelProgress.getState();
      levelProgress.completeNPC(currentMapId, dialog.npcId!);

      // Call completion callback if available
      const state = get();
      if (state.onCompleteCallback) {
        state.onCompleteCallback();
      }

      // Reset dialog state
      get().resetDialogState();

      // Navigate to new map
      const uiStore = useUI.getState();
      uiStore.setCurrentMap(null);
      uiStore.setStartGame(false);
      uiStore.resetLoadingTracker();
      uiStore.initializeLoadingTracker();
      setTimeout(() => {
        uiStore.setCurrentMap(unlockedMapId); // delay to ensure the dialog is closed and full anmount Game component
      }, 0);
    },

    declineMapTransition: () => {
      const dialog = get().dialog;
      const currentMapId = get().currentMapId;

      if (!currentMapId) return;

      // Complete the NPC
      const levelProgress = useLevelProgress.getState();
      levelProgress.completeNPC(currentMapId, dialog.npcId!);

      // Call completion callback if available
      const state = get();
      if (state.onCompleteCallback) {
        state.onCompleteCallback();
      }

      // Reset dialog state
      get().resetDialogState();
    },
  };
});